/* ==========================================================================
   lf-product-main — PDP gallery, variant chips, purchase options, quantity.

   Unit prices are formatted by Liquid: the price line is one pre-rendered
   block per variant that this file toggles, and the shorter strings come from
   the data-lf-prices JSON. The single exception is the Add To Cart line total
   above quantity 1, which Liquid cannot know in advance. That is multiplied
   from the minor-unit figure in the same JSON and rendered through Horizon's
   own money-formatting helper — the arithmetic never sees a display string,
   and at quantity 1 the Liquid string is used verbatim.

   The chips write the form's hidden id input and stop there. Dispatching
   shopify:product:select would be answered by Horizon's product-form component
   with a section-rendering fetch plus morph, which would replace this custom
   markup. Add to cart itself is untouched: the native <product-form-component>
   still owns the submit, the /cart/add.js POST and the cart events that keep
   the header count fresh.
   ========================================================================== */

import { formatMoney } from '@theme/money-formatting';

(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.lfPmReady === 'true') return;
    root.dataset.lfPmReady = 'true';

    initGallery(root);
    initBuyBox(root);
  }

  /* ------------------------------------------------------------------------
     Gallery — one scroll-snap track, thumbnails as jump controls.
     ------------------------------------------------------------------------ */

  function initGallery(root) {
    var stage = root.querySelector('[data-lf-stage]');
    var thumbs = toArray(root.querySelectorAll('[data-lf-thumb]'));
    if (!stage || thumbs.length === 0) return;

    var slides = toArray(stage.querySelectorAll('[data-lf-slide]'));
    if (slides.length === 0) return;

    var current = 0;

    function setActive(index) {
      if (index < 0) index = 0;
      if (index > thumbs.length - 1) index = thumbs.length - 1;
      if (index === current) return;
      current = index;
      thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        var slide = slides[i];
        if (!slide) return;
        stage.scrollTo({ left: slide.offsetLeft - stage.offsetLeft, behavior: 'smooth' });
        setActive(i);
      });
    });

    var timer;
    stage.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          var step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 0;
          if (step <= 0) return;
          setActive(Math.round(stage.scrollLeft / step));
        }, 80);
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------------
     Buy box — variant chips, purchase options, frequency, quantity.
     ------------------------------------------------------------------------ */

  function initBuyBox(root) {
    var idField = root.querySelector('[data-lf-variant-id]');
    var planField = root.querySelector('[data-lf-selling-plan]');
    var atc = root.querySelector('[data-lf-atc]');
    var atcLabel = root.querySelector('[data-lf-atc-label]');
    var atcPrice = root.querySelector('[data-lf-atc-price]');
    var onetimePrice = root.querySelector('[data-lf-onetime-price]');
    var planPrice = root.querySelector('[data-lf-plan-price]');
    var priceWrap = root.querySelector('[data-lf-price]');
    var groups = toArray(root.querySelectorAll('[data-lf-option-group]'));
    var cards = toArray(root.querySelectorAll('[data-lf-card]'));
    var freqChips = toArray(root.querySelectorAll('[data-lf-plan-id]'));

    var pricesNode = root.querySelector('[data-lf-prices]');
    var prices = readJson(pricesNode);
    var moneyFormat = pricesNode ? pricesNode.getAttribute('data-money-format') : '';
    var currency = pricesNode ? pricesNode.getAttribute('data-currency') : '';
    var addLabel = atcLabel ? atcLabel.textContent.trim() : 'Add To Cart';

    var state = {
      variantId: idField ? idField.value : '',
      option: cards.length ? cards[0].getAttribute('data-lf-card') : 'onetime',
      planId: freqChips.length ? freqChips[0].getAttribute('data-lf-plan-id') : '',
      quantity: 1
    };

    /* A card can start selected server-side on a subscription-only product. */
    cards.forEach(function (card) {
      if (card.getAttribute('data-selected') === 'true') {
        state.option = card.getAttribute('data-lf-card');
      }
    });
    freqChips.forEach(function (chip) {
      if (chip.getAttribute('aria-checked') === 'true') {
        state.planId = chip.getAttribute('data-lf-plan-id');
      }
    });

    function selectedValues() {
      var picked = {};
      groups.forEach(function (group) {
        var position = group.getAttribute('data-position');
        var checked = group.querySelector('[aria-checked="true"]');
        if (checked) picked[position] = checked.getAttribute('data-lf-option-value');
      });
      return picked;
    }

    function resolveVariant(picked) {
      var ids = Object.keys(prices);
      for (var i = 0; i < ids.length; i++) {
        var record = prices[ids[i]];
        var options = record.options || [];
        var match = true;
        for (var position in picked) {
          if (!Object.prototype.hasOwnProperty.call(picked, position)) continue;
          if (options[Number(position) - 1] !== picked[position]) {
            match = false;
            break;
          }
        }
        if (match) return { id: ids[i], record: record };
      }
      return null;
    }

    function currentPrices() {
      var record = prices[state.variantId];
      if (!record) return null;
      if (state.option === 'plan' && record.plans && record.plans[state.planId]) {
        return record.plans[state.planId];
      }
      return record;
    }

    /* The label carries the line total, so it is the one figure that has to be
       computed rather than served. Quantity 1 keeps Liquid's own string, and
       anything above it multiplies the minor-unit price and re-formats through
       the theme's money helper, which mirrors the `money` filter. A record with
       no usable cents figure falls back to the served string rather than
       showing a wrong total. */
    function lineTotal(money) {
      if (!money) return '';
      var quantity = state.quantity;
      if (quantity <= 1 || typeof money.cents !== 'number' || !moneyFormat) return money.atc;
      return formatMoney(money.cents * quantity, moneyFormat, currency);
    }

    function render() {
      var record = prices[state.variantId];

      /* Price line: pre-rendered per variant, so only the visible one changes. */
      if (priceWrap) {
        toArray(priceWrap.querySelectorAll('[data-lf-price-for]')).forEach(function (block) {
          block.hidden = block.getAttribute('data-lf-price-for') !== String(state.variantId);
        });
      }

      if (onetimePrice && record) onetimePrice.textContent = record.card;

      if (planPrice && record && record.plans && record.plans[state.planId]) {
        planPrice.textContent = record.plans[state.planId].card;
      }

      cards.forEach(function (card) {
        var selected = card.getAttribute('data-lf-card') === state.option;
        card.setAttribute('data-selected', selected ? 'true' : 'false');
        var hit = card.querySelector('[data-lf-purchase-option]');
        if (hit) {
          hit.setAttribute('aria-checked', selected ? 'true' : 'false');
          hit.setAttribute('tabindex', selected ? '0' : '-1');
        }
      });

      if (planField) {
        if (state.option === 'plan' && state.planId) {
          planField.disabled = false;
          planField.value = state.planId;
        } else {
          planField.disabled = true;
          planField.value = '';
        }
      }

      var available = record ? record.available !== false : true;
      if (atc) atc.disabled = !available;
      if (atcLabel) atcLabel.textContent = available ? addLabel : 'Sold Out';
      if (atcPrice) {
        var money = currentPrices();
        atcPrice.hidden = !available || !money;
        if (money) atcPrice.textContent = '· ' + lineTotal(money);
      }
    }

    /* Weight chips. */
    groups.forEach(function (group) {
      var chips = toArray(group.querySelectorAll('[data-lf-option-value]'));
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          /* Resolve first: a combination with no variant must not repaint the chips. */
          var picked = selectedValues();
          picked[group.getAttribute('data-position')] = chip.getAttribute('data-lf-option-value');
          var found = resolveVariant(picked);
          if (!found) return;

          chips.forEach(function (other) {
            var on = other === chip;
            other.setAttribute('aria-checked', on ? 'true' : 'false');
            other.setAttribute('tabindex', on ? '0' : '-1');
          });

          state.variantId = found.id;
          if (idField) idField.value = found.id;
          render();
          updateUrl(found.id);
        });
      });
      addRovingKeys(chips);
    });

    /* One-time against subscribe. */
    cards.forEach(function (card) {
      var hit = card.querySelector('[data-lf-purchase-option]');
      if (!hit) return;
      hit.addEventListener('click', function () {
        state.option = hit.getAttribute('data-lf-purchase-option');
        render();
      });
    });

    /* Delivery frequency. */
    freqChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        freqChips.forEach(function (other) {
          var on = other === chip;
          other.setAttribute('aria-checked', on ? 'true' : 'false');
          other.setAttribute('tabindex', on ? '0' : '-1');
        });
        state.planId = chip.getAttribute('data-lf-plan-id');
        state.option = 'plan';
        render();
      });
    });
    addRovingKeys(freqChips);

    /* Quantity feeds the label's line total, so every change re-renders. */
    state.quantity = initQuantity(root, function (quantity) {
      state.quantity = quantity;
      render();
    });

    render();
  }

  /* Returns the quantity the control starts on and calls back on every change.
     Min and max come from the input's own attributes when the markup sets them,
     so the clamp has one source rather than a number hard-coded here. */
  function initQuantity(root, onChange) {
    var input = root.querySelector('[data-lf-qty-input]');
    var down = root.querySelector('[data-lf-qty-down]');
    var up = root.querySelector('[data-lf-qty-up]');
    if (!input) return 1;

    var min = toBound(input.getAttribute('min'), 1);
    var max = toBound(input.getAttribute('max'), Infinity);
    if (max < min) max = min;

    function clamp(n) {
      if (isNaN(n)) n = min;
      if (n < min) n = min;
      if (n > max) n = max;
      return n;
    }

    function read() {
      return clamp(parseInt(input.value, 10));
    }

    /* Writing the input and reporting are one step, so the field and the label
       can never disagree. */
    function commit(n) {
      var next = clamp(n);
      input.value = String(next);
      onChange(next);
    }

    if (down) {
      down.addEventListener('click', function () {
        commit(read() - 1);
      });
    }
    if (up) {
      up.addEventListener('click', function () {
        commit(read() + 1);
      });
    }

    /* Typing updates the label live, but the field is left as typed until blur
       so the clamp never fights the caret mid-entry. An empty field reads as
       the minimum for pricing purposes and is filled in on change. */
    input.addEventListener('input', function () {
      var cleaned = input.value.replace(/[^0-9]/g, '');
      if (cleaned !== input.value) input.value = cleaned;
      onChange(read());
    });
    input.addEventListener('change', function () {
      commit(read());
    });

    return read();
  }

  function toBound(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */

  function addRovingKeys(items) {
    if (items.length < 2) return;
    items.forEach(function (item, index) {
      item.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = items[index + 1] || items[0];
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = items[index - 1] || items[items.length - 1];
        if (!next) return;
        event.preventDefault();
        next.focus();
        next.click();
      });
    });
  }

  function updateUrl(variantId) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      /* A malformed location is not worth breaking the picker over. */
    }
  }

  function readJson(node) {
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function boot() {
    toArray(document.querySelectorAll('[data-lf-product-main]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-lf-product-main]');
    if (root) init(root);
  });
})();
